import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import Loadable from 'react-loadable';
import { getFileSize, getPlatformName, i18nIntl, isArray, isNotEmpty, milesNumber, throwErrorEvent } from '../../lib/ep.js';
import { useAccount } from '../context/accountContext.js';

import { MainButton, SquaredButton } from '../builder/ActionButton.js';
import ActionLink from '../builder/ActionLink.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import DashboardWithGraphs from '../builder/DashboardWithGraphs.js';
import FloatingLayer from '../builder/FloatingLayer.js';
import NavigationTabs from '../builder/NavigationTabs.js';
import Select from '../builder/Select.js';
import SinglePromoInsight from '../insights/SinglePromoInsight.js';
import Spinner from '../builder/Spinner.js';
import TabledItemsList, { Column } from '../builder/TabledItemsList.js';

import ExportModal from '../backend/ExportModal.js';

import Module from '../ui_items/Module.js';
import SectionHeader from '../ui_items/SectionHeader.js';
import ChartInfo from '../insights/ChartInfo.js';

const section_values = {
	sweep:{
		icon:'knk-present-fill',
		title:"Completed draws",
	},
	user:{
		icon:'knk-user',
		title:"Registered users",
	},
	participation:{
		icon:'knk-entries',
		title:"Completed entries",
	},
	page:{
		icon:'knk-chart-serial',
		title:"Impressions",
	},
	email:{
		icon:'knk-email',
		title:"Emails sent",
	},
	ocr:{
		icon:'knk-buy-ticket',
		title:"OCR scans",
	},
	recaptcha:{
		icon:'knk-list',
		title:"reCAPTCHA validations",
	},
	storage:{
		icon:'knk-folder',
		title:"Storage (SuperAdmins)",
	},
};

export const SummaryStats = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const totals = useRef(null);
	const [ period, setPeriod ] = useState(1);
	const [ loaded, setLoaded ] = useState(false);

	const is_local = (window.location.hostname.indexOf('2.local.easypromosapp.com') != -1) ? true : false;

	const { MyAccount } = useAccount();
	const account_id = MyAccount.getAccountId();
	const language = MyAccount.getAgentLang();
	const now = new Date();

	const is_current_month = (period == 1) ? true : false;

	const displayed_year = now.getFullYear() - ((!is_current_month) ? 1 : 0);
	const actual_month_name = is_current_month ? `${i18n(now.toLocaleString('en', { month:'long' }))} ${displayed_year}` : i18n("last 12 months");

	const getStats = (cb) => {
		setLoaded(false);
		$.get(`/v24/monthly_summary_stats/${account_id}/`, (res) => {
			if(res.status) {
				totals.current = res?.data?.totals ?? {};
			}
			setLoaded(true);
			if(cb != undefined) cb(res.status);
		}, 'JSON').error((xhr, ajaxOptions, thrownError) => {
		 	throwErrorEvent(xhr.status);
		 });
	};
	
	useEffect(() => {
		if(!totals.current && !is_local) getStats();
	}, []);

	if(is_local){
		return (
			<div>
				Stats not shown in local environment.
			</div>
		)
	}

	if(!loaded) {
		return (
			<div class="gui_pos_rel" style={{ height:'60px' }}>
				<Spinner>Loading stats</Spinner>
			</div>
		)
	}

	const has_stats = (Object.values(totals.current).reduce((acc, value) => acc + parseInt(value), 0) > 0) ? true : false;

	if(!has_stats) {
		return(
			<div class="gui_pos_rel" style={{ height:'60px', display:'flex', justifyContent:'center', alignItems:'center' }}>
				{i18n("No data")}.
			</div>
		)
	}

	const orderedSections = [];
	Object.keys(section_values).forEach(key => {
		const filteredEntries = Object.entries(totals.current).filter(([k, v]) => k.startsWith(key));
		orderedSections.push({
			id:key,
			title: section_values[key].title,
			icon: section_values[key].icon,
			data: Object.fromEntries(filteredEntries)
		});
	});

	const label_period = (is_current_month) ? "Switch to last 12 months" : "Switch to current month";
	return (
		<div>
			<p>{i18n("Activity from")} {actual_month_name}</p>

			<p><ActionLink onClick={() => setPeriod(is_current_month ? 12 : 1)}>{label_period}</ActionLink></p>

			<ul class="gui_simple_iconed_list gui_not_in_top gui_not_in_bottom_big">

				{(orderedSections).map((item, i) => {
					const period_value = item?.data?.[`${item.id}-${period}`] ?? 0;

					const annual_value = parseInt(item?.data?.[`${item.id}-12`] ?? 0);
					if(period_value == 0 && annual_value == 0 || item.id == 'storage' && !window.is_admin)return null
					const result = (item.id != 'storage') ? (period_value > 0 ? milesNumber(period_value) : 0) : getFileSize(period_value, 2, 'MB');

					return (
						<li
							key={`stats-${item.id}`}
							class={item.icon}
						>{i18n(item.title)}: <strong>{result}</strong></li>
					)

				})}
			</ul>

			<ButtonCollection
				_class={{ other:'gui_flex_center' }}
			>
				<ActionLink
					_href={`${window.panel_url}usage_stats`}
				>View full statistics</ActionLink>
			</ButtonCollection>
		</div>
	)
};

const InsightsElements = Loadable({
	loader:() => import('../insights/InsightsElements'),
	loading:() => <div>Loading...</div>,
});

const EmptySection = ({ title, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	return (
		<Module
			_class={{ other:'not_in_top' }}
		>
			<p>{props.intl.formatMessage({ id:"No statistics data available for {title}" }, { title:i18n(title,'lowercase') })}</p>
		</Module>
	)
};

const SingleTablePromotion = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const section = props?.section ?? false;
	if(section === false) return null;
	
	return (
		<TabledItemsList
			{...props}
			columns={[
				<Column
					title={i18n("ID")}
					children={(item) => {
						return (
							<div>
								{item?.promotion_id ?? "-"}
							</div>
						)
					}}
				/>,
				<Column
					main={true}
					title={i18n("Promotion")}
					children={(item) => {
						const base_url = (item.platform_id) ? getPlatformName(item.platform_id, false, true) : '';
		
						return (
							<div style={{ height:'1.3em' }}>
								<ActionLink
									_href={`${base_url}promotions/profile/${item.promotion_id}`}
									_target="_blank"
									_class={{ other:'gui_cutTextOnOverflow' }}
									autotranslate={false}
								>{item.title}</ActionLink>
							</div>
						)
					}}
				/>,
				<Column
					title={i18n("Type of promotion")}
					children={(item) => {
						const promotion_type = (props?.promotion_types ?? []).find((pt) => (pt.id == item.promotion_type_id));
						const field_value = (promotion_type.name != undefined) ? i18n(promotion_type.name) : `${i18n("Unknown")}: ${item.promotion_type_id}`;
		
						return (
							<div>
								{field_value}
							</div>
						)
					}}
				/>,
				<Column
					title={i18n("Organizing brand")}
					children={(item) => {
						const brand = (props?.brands ?? []).find((pt) => (pt.id == item.organizing_brand_id));
						const field_value = brand?.name ?? ((item.organizing_brand_id) ? `#${item.organizing_brand_id}` : i18n("Without organizing brand"));
		
						return (
							<div>
								{field_value}
							</div>
						)
					}}
				/>,
				<Column
					title={i18n("Total")}
					type='number'
					class="gui_text_align_end"
					children={(item) => {
						return (
							<div>
								{(section != 'storage') ? milesNumber(item.value) : getFileSize(item.value, 2, 'MB')}
							</div>
						)
					}}
				/>,
				<Column
					type='actions'
					class="gui_text_align_end"
					children={(item) => {
						return (
							<ButtonCollection _class={{ other:'gui_flex_end' }}>
								<SquaredButton
									icon='knk-stats'
									onClick={() => props.viewInsight(section, item, 'promotion')}
								/>
							</ButtonCollection>
						)
					}}
				/>,
			]}
			empty_message={i18n("There are no results.")}
			max_height='calc(100dvh - 390px - 1em)'
			column_defs={{
				gridTemplateColumns:props?.column_defs ?? '100px 1fr 200px 200px 100px 50px',
			}}
			_class={{ other:'not_in_top' }}
		/>
	)
};

const SingleTableSweep = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const { MyAccount } = useAccount();
	
	const section = props?.section ?? false;
	if(section === false) return null;
	
	return (
		<TabledItemsList
			{...props}
			columns={[
				<Column
					main={true}
					title={i18n("Title")}
					children={(item) => {
						const base_url = (item.platform_id) ? getPlatformName(item.platform_id, false, true) : '';

						let item_url;
						if(item.sweep_type == 'promotion') {
							item_url = `${base_url}prizes/manager/${item.promotion_id}`;
						} else {
							item_url = `${window.panel_url}sweeps/success/${item.sweep_id}`;
						}

						return (
							<div style={{ height:'1.3em' }}>
								<ActionLink
									_href={item_url}
									_target="_blank"
									_class={{ other:'gui_cutTextOnOverflow' }}
									autotranslate={false}
								>{item.title}</ActionLink>
							</div>
						)
					}}
				/>,
				<Column
					title={i18n("Type")}
					children={(item) => {
						let field_value;
						switch(item.sweep_type) {
							case 'promotion':
								field_value = i18n("Promotion");
								break;

							case 'multi':
								field_value = i18n("Multi-Network");
								break;

							case 'list':
								field_value = i18n("Pick-A-Winner App");
								break;

							case 'live':
								field_value = i18n("Event Giveaway");
								break;

							default:
								field_value = i18n(item.sweep_type, 'ucfirst');
								break;
						}
						return (
							<div style={{ height:'1.3em' }}>
								{field_value}
							</div>
						)
					}}
				/>,
				<Column
					title={i18n("Organizing brand")}
					children={(item) => {
						const brand = (props?.brands ?? []).find((pt) => (pt.id == item.organizing_brand_id));
						const field_value = brand?.name ?? ((item.organizing_brand_id) ? `#${item.organizing_brand_id}` : i18n("Without organizing brand"));
		
						return (
							<div>
								{field_value}
							</div>
						)
					}}
				/>,
				<Column
					title={i18n("Finalists")}
					type='number'
					class="gui_text_align_end"
					children={(item) => {
						return (
							<div>
								{milesNumber(item.value)}
							</div>
						)
					}}
				/>,
				<Column
					title={i18n("Date of Draw")}
					type='date'
					children={(item) => {
						return (
							<div>
								{EPGetTimezone(item.making_date, MyAccount.getAgentTimezone(), MyAccount.getAgentDateTimeFormat())}
							</div>
						)
					}}
				/>,
			]}
			empty_message={i18n("There are no results.")}
			max_height='calc(100dvh - 390px - 1em)'
			column_defs={{
				gridTemplateColumns:props?.column_defs ?? ' 1fr 160px 225px 100px 160px',
			}}
			_class={{ other:'not_in_top' }}
		/>
	)
};

const SingleTableBrand = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const section = props?.section ?? false;
	if(section === false) return null;
	
	return (
		<TabledItemsList
			{...props}
			columns={[
				<Column
					title={i18n("ID")}
					children={(item) => {
						return (
							<div>
								{item?.id ?? "-"}
							</div>
						)
					}}
				/>,
				<Column
					title={i18n("Organizing brand")}
					children={(item) => {
						return (
							<div style={{ height:'1.3em' }}>
								<ActionLink
									_href={`${window.panel_url}brands#${item.id}/promos`}
									_target="_blank"
									_class={{ other:'gui_cutTextOnOverflow' }}
									autotranslate={false}
								>{(isNotEmpty(item, 'title')) ? item.title : i18n("Without organizing brand")}</ActionLink>
							</div>
						)
					}}
				/>,
				<Column
					title={i18n("Total")}
					type='number'
					class="gui_text_align_end"
					children={(item) => {
						return (
							<div>
								{(section != 'storage') ? milesNumber(item.value) : getFileSize(item.value, 2, 'MB')}
							</div>
						)
					}}
				/>,
				<Column
					type='actions'
					class="gui_text_align_end"
					children={(item) => {
						if(!item.id)return null
						return (
							<ButtonCollection _class={{ other:'gui_flex_end' }}>
								<SquaredButton
									icon='knk-stats'
									onClick={() => props.viewInsight(section, item, 'brand')}
								/>
							</ButtonCollection>
						)
					}}
				/>,
			]}
			empty_message={i18n("There are no results.")}
			max_height='calc(100dvh - 390px - 1em)'
			column_defs={{
				gridTemplateColumns:props?.column_defs ?? '100px 1fr 100px 50px',
			}}
			_class={{ other:'not_in_top' }}
		/>
	)
};

const SingleTableTitleValue = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const section = props?.section ?? false;
	if(section === false) return null;

	return (
		<TabledItemsList
			{...props}
			columns={[
				<Column
					title={i18n("ID")}
					children={(item) => {
						return (
							<div>
								{item?.id ?? "-"}
							</div>
						)
					}}
				/>,
				<Column
					title={i18n("Type")}
					children={(item) => {
						return (
							<div>
								<strong>{(isNotEmpty(item, 'title')) ? item.title : '-'}</strong>
							</div>
						)
					}}
				/>,
				<Column
					title={i18n("Total")}
					type='number'
					class="gui_text_align_end"
					children={(item) => {
						return (
							<div>
								{(section != 'storage') ? milesNumber(item.value) : getFileSize(item.value, 2, 'MB')}
							</div>
						)
					}}
				/>,
			]}
			empty_message={i18n("There are no results.")}
			max_height='calc(100dvh - 390px - 1em)'
			column_defs={{
				gridTemplateColumns:props?.column_defs ?? '100px 1fr 100px',
			}}
			_class={{ other:'not_in_top' }}
		/>
	)
};

const TableStats = (props) => {
	const section = props?.section ?? false;
	if(section === false) return null;

	const type = props?.type ?? 'promotion';
	const header = props?.header ?? {};
	const period = props?.period ?? 0;
	const display_actions = props?.display_actions ?? false;

	const display_stats = (['user', 'participation'].includes(section) && type == 'promotion') ? true : false;

	const stats_props = {
		table_id:type,
		items:props?.data ?? [],
		section,
		display_actions,
		display_stats:(display_actions && display_stats) ? true : false,
		promotion_types:props.promotion_types,
		brands:props.brands,
		viewInsight:props.viewInsight,
		intl:props.intl,
	};

	const graph = ((props?.graphs ?? []).length > 0) ? props.graphs[0] : {};

	return (
		<Module
			key={`${type}_${section}_${period}`}
			title={props?.title ?? (header?.title ?? "Unknown title")}
			icon={false}
			actions={header.actions}
			action_container_props={{
				style:{
					position:'relative',
					bottom:'0.5em',
				},
			}}
			autotranslate={false}
			_class={{ other:'not_in_top' }}
		>
			{/*<ChartInfo
				updated={graph?.updated ?? ''}
				extra={((graph?.total_registers ?? 0) > 0) ? {
					total_registers:graph.total_registers,
					total_unique_registers:graph.total_unique_registers,
				} : null}
			/>*/}

			<div id={`${type}_${section}_${period}_table`}>
				{(type == 'promotion') && (
					<SingleTablePromotion
						{...stats_props}
					/>
				)}

				{(type == 'sweep') && (
					<SingleTableSweep
						{...stats_props}
					/>
				)}

				{(type == 'brand') && (
					<SingleTableBrand
						{...stats_props}
					/>
				)}

				{(type == 'title_value') && (
					<SingleTableTitleValue
						{...stats_props}
					/>
				)}
			</div>

			{/*props.children*/}
		</Module>
	)
};

const ChartStats = (props) => {
	const graphs = props?.graphs ?? [];
	if(graphs.length == 0) return null;

	const section = props?.section ?? false;
	if(section === false) return null;
	
	const header = props?.header ?? {};
	const type = props?.type ?? 'promotions';
	const display_total = props?.display_total ?? false;
	const display_last_update = props?.display_last_update ?? false;
	
	const with_export = header.id == 'sweep_evolution';
	return (
		<Module
			title={props?.title ?? (header?.title ?? null)}
			subtitle={props?.subtitle ?? (header?.subtitle ?? null)}
			icon={false}
			actions={header.actions}
			action_container_props={{
				style:{
					position:'relative',
					bottom:'0.5em',
				},
			}}
			autotranslate={false}
			_class={{ other:'not_in_top' }}
		>
			{(header?.last_updated ?? false) && (
				<div
					class={`${with_export ? `gui_is_padded_low`: `gui_is_padded_big`}`}
					style={{
						position:'absolute',
						top:'0',
						right:(with_export) ? '1.5em' : '0',
					}}
				>
					<ChartInfo
						updated={header.last_updated}
						display_total={false}
						extra={{
							total_registers:true,	//forçar visibilitat
						}}
					/>
				</div>
			)}

			{graphs.map((graph) => {
				const graph_data = graph?.data ?? {};
				const total_graphs = (isArray(graph_data)) ? graph_data.length : Object.keys(graph_data).length;
				if(total_graphs == 0) return null;

				// If the pie chart will have only 1 slice, it's irrelevant because it's exactly 100% and the table is already there
				const has_relevant_data = (graph_data[Object.keys(graph_data)[0]].data.length > 1) ? true : false;
				if(!has_relevant_data && false) return null;

				return (
					<div
						key={`${type}_${header.id}`}
						id={header.id}
						class="gui_pos_rel"
						style={(props.is_additional_chart ?? false) ? {
							marginBlockStart:'1em',
							padding:'1em',
							border:'1px dashed rgba(var(--main-text-color), 0.2)',
							borderRadius:'0.3em',
							backgroundColor:'rgba(var(--main-text-color), 0.03)',
						} : null}
					>
						<InsightsElements
							name={`${type}_${header.id}`}
							updated={graph?.updated ?? ''}
							graphs={graph_data}
							extra={((graph?.total_registers ?? 0) > 0) ? {
								total_registers:graph.total_registers,
								total_unique_registers:graph.total_unique_registers,
							} : null}
							active={header}
							main_bar_color={props.main_bar_color}
							limit_slices={9}
							display_total={display_total}
							display_last_update={display_last_update}
							{...props.default_graph_props}
						/>
					</div>
				)
			})}
		</Module>
	)
};

const Wrapper = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	return (
		<div class="usage_stats gui_not_in_bottom">
			<SectionHeader
				title={i18n("Usage statistics")}
				subtitle={<p>{i18n("Account usage overview. This section allows you to compare the account performance month by month based on various parameters, such as registered users, completed entries, emails sent, among others. It will also provide an overview of organized promotions and completed raffles.")} <ActionLink _href={`${window.zendesk_base_url}31558402202132`} _target="_blank" _class={{other:"gui_underlined_text"}}>More information</ActionLink></p>}
				icon={{ icon:'knk-stats' }}
			/>

			{props.children}
		</div>
	)
};

const UsageStats = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const all_data = useRef({});
	const sections = useRef([]);
	
	const [ default_periods, setDefaultPeriods ] = useState({});
	const [ actual_section, setActualSection ] = useState(null);
	const [ display_export, setDisplayExport ] = useState(null);
	const [ loaded, setLoaded ] = useState(false);
	const [ loaded_section, setLoadedSection ] = useState(actual_section);
	const [ display_insight, setDisplayInsight ] = useState(null);

	const { MyAccount } = useAccount();

	const is_superadmin = props?.account_id ?? false;
	const account_id = is_superadmin ? props?.account_id : MyAccount.getAccountId();

	let basename = '/usage_stats';
	if(props?.account_id ?? false) basename += `/${props.account_id}`;
	
	const setSections = () => {
		return [{
			label:"Overview",
			icon:'knk-item-active',
			full_link:'',
			router:true,
		},{
			label:section_values.sweep.title,
			icon:section_values.sweep.icon,
			full_link:'sweep',
			router:true,
			enabled:all_data.current?.totals?.[`sweep-evolution`] ?? false
		},{
			label:section_values.user.title,
			icon:section_values.user.icon,
			full_link:'user',
			router:true,
			enabled:all_data.current?.totals?.[`user-evolution`] ?? false
		}, {
			label:section_values.participation.title,
			icon:section_values.participation.icon,
			full_link:'participation',
			router:true,
			enabled:all_data.current?.totals?.[`participation-evolution`] ?? false
		},{
			label:section_values.page.title,
			icon:section_values.page.icon,
			full_link:'page',
			router:true,
			enabled:all_data.current?.totals?.[`page-evolution`] ?? false
		},{
			label:section_values.email.title,
			icon:section_values.email.icon,
			full_link:'email',
			router:true,
			enabled:all_data.current?.totals?.[`email-evolution`] ?? false
		}, {
			label:section_values.ocr.title,
			icon:section_values.ocr.icon,
			full_link:'ocr',
			router:true,
			enabled:all_data.current?.totals?.[`ocr-evolution`] ?? false
		}, {
			label:section_values.recaptcha.title,
			icon:section_values.recaptcha.icon,
			full_link:'recaptcha',
			router:true,
			enabled:all_data.current?.totals?.[`recaptcha-evolution`] ?? false
		}, {
			label:section_values.storage.title,
			icon:section_values.storage.icon,
			full_link:'storage',
			router:true,
			enabled:(is_superadmin && all_data.current?.totals?.[`storage-evolution`]) ?? false
		}];
	};

	const changeDefaultPeriods = (type, value) => {
		let _default_periods = {...default_periods};
		_default_periods[type] = value;
		setDefaultPeriods(_default_periods);
	}

	const getStats = (type,cb) => {
		setLoaded(false);
		setLoadedSection(type);
		$.get(`/v24/monthly_metric_stats/${account_id}/${type}`, (res) => {
			if(res.status) {
				const new_data = {}
				new_data[type] = res.data?.[type] ?? [];
				if(type != 'totals' && all_data.current.totals == undefined) new_data.totals = res.data.totals;
				all_data.current = { ...all_data.current, ...new_data };
				sections.current = setSections();
			}

			setLoaded(true);
			setLoadedSection(type);

			if(cb != undefined) cb(res.status);
		}, 'JSON').fail((xhr, ajaxOptions, thrownError) => {
			throwErrorEvent(xhr.status);
		});
	};

	const loadedChart = (type, status) => {
		//console.log('Callback Loaded: ',type, status)
	}

	const initChart = (type, status) => {
		//console.log('Callback Initated: ',type, status)
	}

	const resetPeriodSections = () => {
		const defaults = {}
			defaults['promotion_table'] = 12;
			defaults['promotion_graph'] = 12;
			defaults['brand_table'] = 12;
			defaults['brand_graph'] = 12;
			defaults['sweep_table'] = 12;
			defaults['sweep_graph'] = 12;
			defaults['type_graph'] = 12;
		setDefaultPeriods(defaults);
	}

	useEffect(() => {
		if(actual_section && all_data.current[actual_section] == undefined) {
			getStats(actual_section, (status) => { });
		}
		resetPeriodSections();
	}, [ actual_section ]);

	const viewInsight = (type, item, source) => {
		let extra_insights = {};

		let active = main_graphs.find((s) => (s.id == type));
		let title = props.intl.formatMessage({ id:"{title} for {name}" }, { title:i18n(active.title), name:item.title });
		if(title)extra_insights['active'] = {active, ...{title}}

		switch (source) {
			case 'promotion':
				extra_insights['platform_id'] = item.platform_id;
				extra_insights[source] = {
					id:item.promotion_id,
					promo_type:item.platform_id,
					timezone:item?.timezone ?? 'Europe/Madrid',
				}
				break;
			case 'brand':
				extra_insights[source] = {
					id:item.id,
				}
				break;
		}
		

		setDisplayInsight({
			type,
			usage_type:true,
			platform_id:item.platform_id,
			account_id,
			...extra_insights
		});
	};

	useEffect(() => {
		//getStats('totals',(status) => { });
	}, []);

	const main_graphs = [{
		id:'sweep',
		title:"Completed draws",
		icon:'knk-present-fill',
		description:"Includes any selection of random winners that has concluded and generated a validity certificate. This encompasses giveaways on Facebook, Instagram, X (formerly Twitter), multi-network giveaways, listing and live giveaways, as well as prize draws in any type of promotion.",
		color:'var(--ep-brand-color)',
	},{
		id:'user',
		title:"Registered users",
		icon:'knk-user',
		description:"Total number of unique users registered in the promotions. This includes those who have registered through registration forms, as well as users registered via API or SSO. Users coming from social interactions (such as comments on social media raffles) are not included.",
		color:'var(--ep-green-color)',
	}, {
		id:'participation',
		title:"Completed entries",
		icon:'knk-entries',
		description:"Actions taken by registered users within the participation dynamics of a promotion. An entry is considered completed when a user performs actions such as playing a game, spinning the wheel, uploading a photo in a contest, voting for a video, or submitting a purchase receipt, among other activities.",
		color:'var(--telegram-color)',
	}, {
		id:'page',
		title:"Impressions",
		icon:'knk-chart-serial',
		description:"Total number of page views for the promotions conducted in an account. This count does not include views of social media posts, but only impressions from the promotion's website pages. This also includes internal pages such as registration, game viewing, prize pages, and more.",
		color:'var(--ep-orange-color)',
	}, {
		id:'email',
		title:"Emails sent",
		icon:'knk-email',
		description:"Number of emails generated by the user through the promotions email platform. This includes automated emails sent after participation or registration in a promotion, after winning a prize, as well as manual or scheduled emails sent to groups of registered users. Emails sent for email address verification are not included in this count.",
		color:'var(--ep-red-color)',
	}, {
		id:'ocr',
		title:"OCR scans",
		icon:'knk-buy-ticket',
		description:"Each time a receipt is scanned to validate its authenticity and allow participation in a promotion, it counts as one scan.",
		color:'var(--ep-purple-color)',
	}, {
		id:'recaptcha',
		title:"reCAPTCHA validations",
		icon:'knk-list',
		description:"Total number of validations performed to confirm that participants in the promotions are human and not bots. This tool is used to protect promotions from automated registrations and ensure the integrity of the participation process.",
		color:'var(--ep-yellow-color)',
	}, {
		id:'storage',
		title:"Data storage",
		icon:'knk-folder',
		description:"Only for Super Admins",
		color:'var(--ep-black-color)',
		units:"MB",
		expected_factor:0.1,
		disabled:!is_superadmin//NOmes per superadmins
	}];

	const table_stats_props = {
		intl:props.intl,
		promotion_types:props.promotion_types,
		brands:props.brands,
	};

	const wrapper_props = {
		intl:props.intl,
	};

	if(loaded_section == 'totals' && !loaded) {
		return (
			<Wrapper
				{...wrapper_props}
			>
				<Module
					_class={{ other:'not_in_top' }}
				>
					<Spinner>Loading stats</Spinner>
				</Module>
			</Wrapper>
		)
	}

	const default_graph_props = {
		display_header:false,
		loaded:true,
		export:false,
		graph_styles:{
			width:'100%',
			height:'300px',
		},
		intl:props.intl,
		initChart,
		loadedChart
	};

	const periods = [
		{ k:0, v:i18n("Current month") },
		{ k:3, v:props.intl.formatMessage({ id:"Last {month} months" }, { month:3 }) },
		{ k:6, v:props.intl.formatMessage({ id:"Last {month} months" }, { month:6 }) },
		{ k:12, v:props.intl.formatMessage({ id:"Last {month} months" }, { month:12 }) },
	];

	const label_evolution = "Evolution of {type} in the last 12 months by {type_stats}";
	const label_top = "Top 5 {type_graph} by {type}";
	const label_graph = "Distribution of {type_graph} by {type}";
	
	const navigation_tabs_props = {
		sections:sections.current,
		display_type:'tab',
		style:{
			position:'sticky',
			top:'var(--header-h)',
			zIndex:'99',
		},
	};
	return (
		<Wrapper
			{...wrapper_props}
		>
			<Router basename={basename}>
				<Switch>
					<Route exact={true} path="/" render={(route) => {
						setActualSection('totals');
						const has_data = (Object.keys(all_data.current?.totals ?? {})).length > 0;

						if(!has_data) {
							return (
								<>
									<NavigationTabs
										{...navigation_tabs_props}
										active='totals'
									/>

									<Module
										title={false}
										_class={{ other:'gui_flex_align_center gui_bigger_text' }}
										>
										<h3 class="not_in_top">{i18n("Your account does not have any statistics to display yet.")}</h3>
										<p class="not_in_top not_in_bottom"><ActionLink
											_href={`${window.panel_url}promotions/type_selector`}
											_class={{other:'gui_bigger_text gui_underlined_text'}}
										>Create your first promotion</ActionLink>
										</p>
									</Module>
								</>
							)
						}

						return (
							<>
								<NavigationTabs
									{...navigation_tabs_props}
									active='/'
								/>

								<Module
									title="Account usage overview"
									icon='knk-level-hard'
									_class={{ other:'not_in_top not_in_bottom' }}
								/>

								<DashboardWithGraphs
									modules={main_graphs}
									data={all_data.current}
									basepath={`${basename}/`}
									intl={props.intl}
								/>
							</>
						)
					}} />

					<Route path="/(participation|user|email|page|ocr|recaptcha|storage)" exact={true} render={(route) => {
						const section = route.match.params[0];
						const section_path = `/${section}`;
						if(section == 'storage' && !is_superadmin)return null;
						setActualSection(section);

						const section_info = main_graphs.find((s) => (s.id == section));

						if((loaded_section == section && !loaded)) {
							return (
								<>
									<NavigationTabs
										{...navigation_tabs_props}
										active={section_path}
									/>

									<Module
										_class={{ other:'not_in_top' }}
										style={{ height:'150px' }}
									>
										<Spinner>
											{props.intl.formatMessage({
												id:"Loading {section}",
											}, {
												section:`"${i18n(section_info.title)}"`,
											})}
										</Spinner>
									</Module>
								</>
							)
						}

						const section_header = main_graphs.find((mg) => (mg.id == section));

						const section_data = all_data.current?.[section] ?? null;
						if(!section_data) return null;

						const top_promotions = section_data?.tops?.promotion ?? [];
						const top_brands = section_data?.tops?.brand ?? [];

						const graph_evolution = all_data.current?.totals?.[`${section}-evolution`] ?? null;


						if(Object.keys(top_promotions).length == 0 && Object.keys(top_brands).length == 0) {
							return (
								<>
									<NavigationTabs
										{...navigation_tabs_props}
										active={section_path}
									/>

									<EmptySection
										title={section_header.title}
										intl={props.intl}
									/>
								</>
							)
						}

						return (
							<>
								<NavigationTabs
									{...navigation_tabs_props}
									active={section_path}
								/>

								<ChartStats
									type={section}
									graphs={[graph_evolution]}
									header={{
										id:`${section}_evolution`,
										title:props.intl.formatMessage({
											id:label_evolution,
										}, {
											type:i18n(section_header.title, 'lowercase'),
											type_stats:i18n("Promotion", 'lowercase'),
										}),
										subtitle:(section_header.description != undefined) ? i18n(section_header.description) : "",
										//last_updated:graph_evolution?.updated ?? null,
									}}
									section={section}
									default_graph_props={default_graph_props}
									main_bar_color={section_header.color}
									display_total={true}
									intl={props.intl}
								/>

								{Object.entries(top_promotions).length > 0 && Object.entries(top_promotions).map(([_period, promotion]) => {
									if(_period != default_periods.promotion_table) return null;

									return (
										<TableStats
											key={`graph_period_${_period}`}
											type='promotion'
											period={_period}
											section={section}
											periods={periods}
											data={promotion.table}
											header={{
												icon:section_header.icon,
												title:props.intl.formatMessage({
													id:label_top,
												}, {
													type:i18n(section_header.title, 'lowercase'),
													type_graph:i18n("promotions"),
												}),
												actions:<Select
													key={`select_graph_period_${_period}`}
													values={periods}
													value={default_periods.promotion_table}
													change={(k, v) => changeDefaultPeriods('promotion_table',v)}
													_class={{ size:60, first:true, other:'not_in_bottom_small unsetted_width' }}
												>Periodicity</Select>
											}}
											viewInsight={viewInsight}
											display_actions={true}
											intl={props.intl}
											{...table_stats_props}
										/>
									)
								})}

								{Object.entries(top_promotions).length > 0 && Object.entries(top_promotions).map(([_period, promotion]) => {
									if(_period != default_periods.promotion_graph) return null;

									return (
										<ChartStats
											type='promotion'
											graphs={[promotion.graph]}
											header={{ 
												id:`${section}_promotion_${_period}`,
												title:props.intl.formatMessage({
													id:label_graph,
												}, {
													type:i18n(section_header.title, 'lowercase'),
													type_graph:i18n("promotions"),
												}),
												actions:<Select
													key={`select_${section}_promotion_${_period}`}
													values={periods}
													value={default_periods.promotion_graph}
													change={(k, v) => changeDefaultPeriods('promotion_graph',v)}
													_class={{ size:60, first:true, other:'unsetted_width' }}
												>Periodicity</Select>
											}}
											section={section}
											is_additional_chart={true}
											default_graph_props={default_graph_props}
											display_total={true}
											display_last_update={false}
											intl={props.intl}
										/>
									)
								})}

								{Object.entries(top_brands).length > 0 && Object.entries(top_brands).map(([_period, brand]) => {
									if(_period != default_periods.brand_table) return;
									return (
										<TableStats
											key={`graph_period_${_period}`}
											type='brand'
											period={_period}
											section={section}
											periods={periods}
											data={brand.table}
											header={{
												icon:section_header.icon,
												actions:<Select
													key={`graph_period_${_period}`}
													values={periods}
													value={default_periods.brand_table}
													change={(k, v) => changeDefaultPeriods('brand_table',v)}
													_class={{ size:60, first:true, other:'unsetted_width' }}
												>Periodicity</Select>,
												title:props.intl.formatMessage({
													id:label_top,
												}, {
													type:i18n(section_header.title, 'lowercase'),
													type_graph:i18n("Organizing brands", 'lowercase'),
												}),
											}}
											viewInsight={viewInsight}
											display_actions={true}
											intl={props.intl}
											{...table_stats_props}
										/>
									)
								})}

								{Object.entries(top_brands).length > 0 && Object.entries(top_brands).map(([_period, brand]) => {
									if(_period != default_periods.brand_graph) return;
									return (
										<ChartStats
											type='brand'
											graphs={[brand.graph]}
											header={{
												id:`${section}_brand_${_period}`,
												title:props.intl.formatMessage({
													id:label_graph,
												}, {
													type:i18n(section_header.title, 'lowercase'),
													type_graph:i18n("Organizing brands", 'lowercase'),
												}),
												actions:<Select
													key={`graph_${section}_brand_${_period}`}
													values={periods}
													value={default_periods.brand_graph}
													change={(k, v) => changeDefaultPeriods('brand_graph',v)}
													_class={{ size:60, first:true, other:'unsetted_width' }}
												>Periodicity</Select>,}}
											section={section}
											is_additional_chart={true}
											default_graph_props={default_graph_props}
											display_total={true}
											display_last_update={false}
											intl={props.intl}
										/>
									)
								})}
							</>
						)
					}} />

					<Route path="/(sweep)" exact={true} render={(route) => {
						const section = route.match.params[0];
						const section_path = `/${section}`;
						setActualSection(section);
						
						const section_info = main_graphs.find((s) => (s.id == section));

						if(loaded_section == section && !loaded) {
							return (
								<>
									<NavigationTabs
										{...navigation_tabs_props}
										active={section_path}
									/>

									<Module
										_class={{ other:'not_in_top' }}
										style={{ height:'150px' }}
									>
										<Spinner>
											{props.intl.formatMessage({
												id:"Loading {section}",
											}, {
												section:`"${i18n(section_info.title)}"`,
											})}
										</Spinner>
									</Module>
								</>
							)
						}

						const section_header = main_graphs.find((mg) => (mg.id == section));

						const section_data = all_data.current?.[section] ?? null;
						if(!section_data)return null;

						const top_sweeps = section_data?.tops?.sweep ?? [];
						const top_types = section_data?.tops?.type ?? [];
						const graph_evolution = all_data.current?.totals?.[`${section}-evolution`] ?? null;

						if(Object.keys(top_sweeps).length == 0 && Object.keys(top_types).length == 0) {
							return (
								<>
									<NavigationTabs
										{...navigation_tabs_props}
										active={section_path}
									/>

									<EmptySection
										title={section_header.title}
										intl={props.intl}
									/>
								</>
							)
						}

						return (
							<>
								<NavigationTabs
									{...navigation_tabs_props}
									active={section_path}
								/>

								<ChartStats
									type={section}
									graphs={[graph_evolution]}
									header={{
										id:`${section}_evolution`,
										title:props.intl.formatMessage({
											id:label_evolution,
										}, {
											type:i18n(section_header.title, 'lowercase'),
											type_stats:i18n("Giveaway", 'lowercase'),
										}),
										subtitle:section_header?.description ?? null,
										//last_updated:graph_evolution?.updated ?? null,
										actions:<MainButton
											onClick={() => setDisplayExport(section)}
											icon='knk-download'
										>Export list of completed draws</MainButton>
									}}
									section={section}
									default_graph_props={default_graph_props}
									main_bar_color={section_header.color}
									display_total={true}
									intl={props.intl}
								/>

								{Object.entries(top_sweeps).length > 0 && Object.entries(top_sweeps).map(([_period, sweep]) => {
									if(_period != default_periods.sweep_table) return null;

									return (
										<TableStats
											key={`graph_period_${_period}`}
											type='sweep'
											period={_period}
											section={section}
											periods={periods}
											data={sweep.table}
											header={{
												icon:section_header.icon,
												title:props.intl.formatMessage({
													id:"Top 5 of {type} by number of finalists",
												}, {
													type:i18n(section_header.title, 'lowercase'),
												}),
												actions:<Select
													key={`select_graph_period_${_period}`}
													values={periods}
													value={default_periods.sweep_table}
													change={(k, v) => changeDefaultPeriods('sweep_table',v)}
													_class={{ size:60, first:true, other:'unsetted_width' }}
												>Periodicity</Select>,
											}}
											viewInsight={viewInsight}
											display_actions={true}
											intl={props.intl}
											{...table_stats_props}
										/>
									)
								})}

								{Object.entries(top_sweeps).length > 0 && Object.entries(top_sweeps).map(([_period, sweep]) => {
									if(_period != default_periods.sweep_graph) return null;

									return (
										<ChartStats
											type='sweep'
											graphs={[sweep.graph]}
											header={{ 
												id:`${section}_sweep_${_period}`,
												title:props.intl.formatMessage({
													id:"Distribution by number of finalists",
												}),
												actions:<Select
													key={`select_${section}_sweep_${_period}`}
													values={periods}
													value={default_periods.sweep_graph}
													change={(k, v) => changeDefaultPeriods('sweep_graph', v)}
													_class={{ size:60, first:true }}
												>Periodicity</Select>,
											}}
											section={section}
											is_additional_chart={true}
											default_graph_props={default_graph_props}
											display_total={true}
											display_last_update={false}
											intl={props.intl}
										/>
									)
								})}

								{Object.entries(top_types).length > 0 && Object.entries(top_types).map(([_period, type]) => {
									if(_period != default_periods.type_graph) return null;

									return (
										<ChartStats
											type='sweep'
											graphs={[type.graph]}
											header={{
												id:`${section}_type_${_period}`,
												title:props.intl.formatMessage({
													id:"Distribution by type",
												}),
												actions:<Select
													key={`graph_${section}_type_${_period}`}
													values={periods}
													value={default_periods.type_graph}
													change={(k, v) => changeDefaultPeriods('type_graph',v)}
													_class={{ size:60, first:true }}
												>Periodicity</Select>,
											}}
											section={section}
											is_additional_chart={true}
											default_graph_props={default_graph_props}
											display_total={false}
											display_last_update={false}
											intl={props.intl}
										/>
									)
								})}
							</>
						)
					}} />
				</Switch>
			</Router>

			{(display_export) && (
				<ExportModal
					toggle={() => setDisplayExport(null)}
					account_id={MyAccount.getAccountId()}
					agent_email={MyAccount.getAgentEmail()}
					type={`usage_${display_export}`}
					filename={`usage_${display_export}_${account_id}`}
					title={`${i18n("Download Type")}: ${i18n("Completed draws")}`}
					download_center={true}
				/>
			)}

			{(display_insight) && (
				<FloatingLayer
					_class={{ size:'fullscreen' }}
					backButton={() => setDisplayInsight(null)}
				>
					<div class="without_actions">
						<SinglePromoInsight
						//	active={main_graphs.find((s) => (s.id == display_insight.type))}
							intl={props.intl}
							{...display_insight}
						/>
					</div>
				</FloatingLayer>
			)}
		</Wrapper>
	)
};

export default injectIntl(UsageStats);